import { useEffect, useState } from "react";
import { AlertTriangle, BadgeCheck, CreditCard, ExternalLink, Loader2, RefreshCw } from "lucide-react";
import { Btn, Section } from "@/components/account/Shell";
import { Cell, Sheet, Tag, Tiles, Two, Txt } from "@/components/account/Sheet";
import api from "@/lib/api";

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const COLS = [
    { key: "shop", label: "Shop", sub: "name · space · city", w: "minmax(220px,1.4fr)" },
    { key: "register", label: "Register", sub: "can they take a card?", w: "170px" },
    { key: "payouts", label: "Payouts", sub: "where Stripe stands", w: "150px" },
    { key: "orders", label: "Card orders", sub: "paid by card", w: "110px" },
    { key: "gross", label: "Paid", sub: "$ through 13thav", w: "120px" },
    { key: "fees", label: "Platform fee", sub: "$ 13thav kept", w: "120px" },
];

function registerChip(s) {
    if (s.house) return <Tag cls="border-[#1a1a1a] bg-[#1a1a1a] text-white bo-inv" testid={`payout-state-${s.slug}`}><BadgeCheck size={9} /> platform register</Tag>;
    if (s.charges_enabled) return <Tag status="active" testid={`payout-state-${s.slug}`}><CreditCard size={9} /> taking cards</Tag>;
    if (s.connected) return <Tag status="pending" testid={`payout-state-${s.slug}`}>onboarding</Tag>;
    return <Tag status="removed" testid={`payout-state-${s.slug}`}>not connected</Tag>;
}

/** Platform admin → Payouts: who can take a card, and what the mall has earned on top. */
export default function PayoutsAdmin() {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = () => api.get("/platform/payouts").then(({ data }) => setData(data)).catch(() => setData({ shops: [] }));
    useEffect(() => { load(); }, []);
    const refresh = async () => { setBusy(true); await load(); setBusy(false); };

    if (!data) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#4a4a4a]" /></div>;

    const t = data.totals || {};
    const shops = data.shops || [];

    return (
        <div className="space-y-3" data-testid="payouts-admin">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <p className="max-w-3xl text-sm text-[#4a4a4a]">
                    Every shop connects its own Stripe and is paid direct. 13thav keeps{" "}
                    <span className="font-semibold text-[#1a1a1a]">{data.fee_pct}%</span> of each paid product order.
                    Classified ads and classes are free — no fee, ever.
                </p>
                <Btn onClick={refresh} disabled={busy} testid="payouts-refresh">
                    {busy ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} refresh
                </Btn>
            </div>

            {data.connect_enabled === false && (
                <div className="flex gap-2.5 border border-[#b54708] bg-[#fffaeb] p-2.5" data-testid="payouts-connect-warning">
                    <AlertTriangle size={16} className="mt-0.5 shrink-0 text-[#b54708]" />
                    <div>
                        <p className="text-sm font-semibold text-[#1a1a1a]">Connect isn't switched on yet</p>
                        <p className="mt-0.5 text-[13px] text-[#4a4a4a]">{data.connect_help}</p>
                        <a href="https://dashboard.stripe.com/connect" target="_blank" rel="noreferrer"
                            className="mt-1 inline-flex items-center gap-1 text-[12px] font-semibold text-[#1a56db] underline">
                            open stripe connect <ExternalLink size={11} />
                        </a>
                    </div>
                </div>
            )}

            <Tiles testid="payout-tiles" items={[
                ["Shops taking cards", `${t.live || 0} of ${shops.length}`, "connected and charging", "payout-stat-shops"],
                ["Card orders paid", t.orders || 0, "across every shop", "payout-stat-card"],
                ["Paid through 13thav", money(t.gross), "gross card volume", "payout-stat-paid"],
                [`Platform fee (${data.fee_pct}%)`, money(t.fees), "what 13thav kept", "payout-stat-platform"],
            ]} />

            <Section title={`${shops.length} shops`} pad={false} testid="payouts-sheet-section" note="one row per shop · the house register never pays itself a fee">
                <Sheet cols={COLS} rows={shops} rowKey={(s) => s.slug} testid="payouts-sheet" minWidth={900}
                    rowTestid={(s) => `payout-row-${s.slug}`} empty="No shops yet."
                    cells={(s) => ({
                        shop: <Cell><Two a={s.name} b={[s.tier_label, s.city].filter(Boolean).join(" · ")} /></Cell>,
                        register: <Cell>{registerChip(s)}</Cell>,
                        payouts: <Cell><Txt sub>{s.house ? "the house" : s.payouts_enabled ? "enabled" : s.connected ? "waiting on stripe" : "—"}</Txt></Cell>,
                        orders: <Cell><Txt mono right>{s.orders}</Txt></Cell>,
                        gross: <Cell><Txt mono right>{money(s.gross)}</Txt></Cell>,
                        fees: <Cell><Txt bold mono right>{money(s.fees)}</Txt></Cell>,
                    })} />
            </Section>
        </div>
    );
}
